import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Heart, ShoppingBag, User, Menu, X, Minus, Plus, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { useFav } from "@/context/FavoritesContext";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/lib/api";

const NAV = [
  { label: "Nouveautés", to: "/catalogue?sort=new" },
  { label: "Vêtements", to: "/c/vetements" },
  { label: "Bijoux", to: "/c/bijoux" },
  { label: "Décoration", to: "/c/decoration" },
  { label: "Cosmétiques & Beauté", to: "/c/cosmetiques" },
  { label: "Nos créateurs", to: "/createurs" },
];

export function Logo({ light = false, className = "" }) {
  return (
    <Link to="/" data-testid="nav-logo" className={`inline-flex items-baseline gap-1 ${className}`}>
      <span className={`font-display text-2xl tracking-[0.18em] ${light ? "text-white" : "text-[#0A0A0A]"}`}>CAGOLE</span>
      <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] translate-y-[-2px]" />
      <span className={`font-display text-2xl tracking-[0.18em] ${light ? "text-white" : "text-[#0A0A0A]"}`}>CHIC</span>
    </Link>
  );
}

function CartDrawer() {
  const { items, open, setOpen, update, remove, subtotal, count } = useCart();
  const nav = useNavigate();
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col" data-testid="cart-drawer">
        <SheetTitle className="sr-only">Votre panier</SheetTitle>
        <div className="px-6 py-5 border-b border-neutral-100 flex items-center justify-between">
          <span className="over text-xs">Votre sélection ({count})</span>
        </div>
        <div className="flex-1 overflow-y-auto px-6 no-scrollbar">
          {items.length === 0 && <p className="text-sm text-neutral-500 py-16 text-center">Votre panier est vide.</p>}
          {items.map((it, i) => (
            <div key={i} className="flex gap-4 py-5 border-b border-neutral-100" data-testid={`cart-line-${i}`}>
              <img src={it.image} alt="" className="w-20 h-24 object-cover bg-neutral-100" />
              <div className="flex-1">
                <p className="over text-[0.6rem] text-[#8E8E93]">{it.creator_name}</p>
                <p className="font-display text-base leading-tight">{it.name}</p>
                {it.variant_label && <p className="text-xs text-neutral-500 mt-0.5">{it.variant_label}</p>}
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center border border-neutral-200">
                    <button onClick={() => update(i, it.quantity - 1)} className="px-2 py-1"><Minus size={13} /></button>
                    <span className="px-3 text-sm">{it.quantity}</span>
                    <button onClick={() => update(i, it.quantity + 1)} className="px-2 py-1"><Plus size={13} /></button>
                  </div>
                  <span className="text-sm">{money(it.price * it.quantity)}</span>
                </div>
              </div>
              <button onClick={() => remove(i)} className="text-neutral-400 hover:text-[#E0115F] self-start"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
        <div className="border-t border-neutral-100 px-6 py-5 space-y-4">
          <div className="flex justify-between text-sm"><span className="text-neutral-500">Sous-total</span><span>{money(subtotal)}</span></div>
          <button disabled={!items.length} data-testid="cart-checkout-button" onClick={() => { setOpen(false); nav("/checkout"); }} className="btn btn-magenta w-full">Passer la commande</button>
          <button onClick={() => { setOpen(false); nav("/panier"); }} className="btn btn-outline w-full">Voir le panier</button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function SearchDrawer({ open, setOpen }) {
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const submit = (e) => { e.preventDefault(); if (q.trim()) { setOpen(false); nav(`/catalogue?q=${encodeURIComponent(q)}`); } };
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="top" className="p-0" data-testid="search-drawer">
        <SheetTitle className="sr-only">Rechercher</SheetTitle>
        <form onSubmit={submit} className="max-w-3xl mx-auto px-6 py-16">
          <span className="over text-xs text-[#8E8E93]">Rechercher</span>
          <div className="flex items-center gap-3 border-b border-neutral-900 mt-4 pb-3">
            <Search size={22} />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-input"
              placeholder="robe noire, rouge à lèvres mat, bague argent…"
              className="flex-1 bg-transparent outline-none font-display text-2xl placeholder:text-neutral-300" />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {["robe noire", "bague argent", "vase céramique", "sérum hydratant", "rouge à lèvres mat"].map((s) => (
              <button type="button" key={s} onClick={() => { setOpen(false); nav(`/catalogue?q=${encodeURIComponent(s)}`); }}
                className="over text-[0.62rem] border border-neutral-200 px-3 py-1.5 hover:border-[#D4AF37]">{s}</button>
            ))}
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

export default function Layout({ children }) {
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);
  const [search, setSearch] = useState(false);
  const { count, setOpen } = useCart();
  const { ids } = useFav();
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  const accountTo = () => {
    if (!user) return "/connexion";
    if (user.role === "admin") return "/admin";
    if (user.role === "seller") return "/espace-createur";
    return "/compte";
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="bg-[#0A0A0A] text-white text-center py-2.5">
        <span className="over text-[0.6rem] text-neutral-300">Livraison offerte & assurée dès 200 € • Créations sélectionnées avec exigence</span>
      </div>

      <header className={`sticky top-0 z-40 transition-colors duration-300 ${scrolled ? "bg-white/90 backdrop-blur-md border-b border-neutral-100" : "bg-white border-b border-transparent"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <div className="flex items-center justify-between h-20">
            <button className="lg:hidden" onClick={() => setMenu(true)} data-testid="mobile-menu-button"><Menu size={22} /></button>
            <Logo className="lg:mr-10" />
            <nav className="hidden lg:flex items-center gap-7 flex-1">
              {NAV.map((n) => (
                <Link key={n.label} to={n.to} className="over text-[0.68rem] text-[#111] link-underline" data-testid={`nav-${n.label}`}>{n.label}</Link>
              ))}
            </nav>
            <div className="flex items-center gap-4">
              <button onClick={() => setSearch(true)} data-testid="nav-search-button"><Search size={19} /></button>
              <Link to="/favoris" className="relative" data-testid="nav-favorites-button">
                <Heart size={19} />
                {ids.length > 0 && <span className="absolute -top-2 -right-2 bg-[#E0115F] text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center">{ids.length}</span>}
              </Link>
              <button onClick={() => nav(accountTo())} data-testid="nav-account-button"><User size={19} /></button>
              <button onClick={() => setOpen(true)} className="relative" data-testid="nav-cart-button">
                <ShoppingBag size={19} />
                {count > 0 && <span className="absolute -top-2 -right-2 bg-[#0A0A0A] text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center">{count}</span>}
              </button>
            </div>
          </div>
        </div>
      </header>

      <Sheet open={menu} onOpenChange={setMenu}>
        <SheetContent side="left" className="w-80 p-0">
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <div className="px-6 py-6 border-b border-neutral-100 flex items-center justify-between">
            <Logo />
            <button onClick={() => setMenu(false)}><X size={20} /></button>
          </div>
          <nav className="flex flex-col px-6 py-4">
            {NAV.map((n) => (
              <Link key={n.label} to={n.to} onClick={() => setMenu(false)} className="font-display text-2xl py-3 border-b border-neutral-50">{n.label}</Link>
            ))}
            <Link to="/devenir-createur" onClick={() => setMenu(false)} className="over text-xs mt-6 text-[#E0115F]">Devenir créateur</Link>
          </nav>
        </SheetContent>
      </Sheet>

      <CartDrawer />
      <SearchDrawer open={search} setOpen={setSearch} />

      <main className="flex-1">{children}</main>

      <footer className="bg-[#0A0A0A] text-white mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16 grid grid-cols-2 md:grid-cols-4 gap-10">
          <div className="col-span-2 md:col-span-1">
            <Logo light />
            <p className="text-sm text-neutral-400 mt-4 leading-relaxed max-w-xs">La galerie éditée des créateurs indépendants. Une sélection exigeante, une seule maison.</p>
          </div>
          <div>
            <p className="over text-[0.62rem] text-[#D4AF37] mb-4">Boutique</p>
            {NAV.map((n) => <Link key={n.label} to={n.to} className="block text-sm text-neutral-300 hover:text-white py-1.5">{n.label}</Link>)}
          </div>
          <div>
            <p className="over text-[0.62rem] text-[#D4AF37] mb-4">Maison</p>
            {[["À propos", "a-propos"], ["Nos créateurs", null], ["Devenir créateur", null], ["Contact", "contact"], ["FAQ", "faq"]].map(([l, s]) => (
              <Link key={l} to={s ? `/page/${s}` : (l === "Devenir créateur" ? "/devenir-createur" : "/createurs")} className="block text-sm text-neutral-300 hover:text-white py-1.5">{l}</Link>
            ))}
          </div>
          <div>
            <p className="over text-[0.62rem] text-[#D4AF37] mb-4">Informations</p>
            {[["Livraison", "livraison"], ["Retours", "retours"], ["CGV", "cgv"], ["Confidentialité", "confidentialite"], ["Mentions légales", "mentions-legales"]].map(([l, s]) => (
              <Link key={l} to={`/page/${s}`} className="block text-sm text-neutral-300 hover:text-white py-1.5">{l}</Link>
            ))}
          </div>
        </div>
        <div className="border-t border-white/10 py-6 text-center">
          <p className="over text-[0.58rem] text-neutral-500">© {new Date().getFullYear()} Cagole Chic — Tous droits réservés</p>
        </div>
      </footer>
    </div>
  );
}
